// Route guard for the /admin/* tree.
//
// The admin surface is sensitive: it exposes reference-data CRUD,
// review queues, bulk imports, and the contractually-significant CSV
// export. We gate the whole tree behind a single wrapper rather than
// letting individual loaders reason about auth, so there is exactly
// one place to audit for "who can see admin pages".
//
// Branching rules (order matters):
//   1. 'loading' → neutral loading state, no navigation yet.
//   2. 'unauthenticated' → redirect out of the SPA to /auth/login so
//      the backend can kick off the Entra ID round-trip. We preserve
//      the current URL via `redirect_to` so the user lands back where
//      they were after sign-in.
//   3. 'error' → surface the error; do NOT redirect to login. A 5xx
//      on /me is not "sign in again", and silently routing to login
//      would mask genuine outages.
//   4. 'authenticated' && role !== 'admin' → <UnauthorizedPage>. Field
//      staff who stumble onto an /admin/* URL get a clear explanation
//      rather than a redirect loop.
//   5. 'authenticated' && role === 'admin' → render children.

import { useEffect, type ReactNode } from 'react'

import { buildLoginUrl, navigateTo } from '../hooks/api'
import { BrandedSplash } from '../ui/branded-splash'

import { useAuth } from './auth-provider'
import { UnauthorizedPage } from './unauthorized-page'

export interface RequireAdminProps {
  children: ReactNode
}

export function RequireAdmin({ children }: RequireAdminProps): ReactNode {
  const { status, user, error } = useAuth()

  useEffect(() => {
    if (status === 'unauthenticated' && typeof window !== 'undefined') {
      const here = `${window.location.pathname}${window.location.search}`
      navigateTo(buildLoginUrl(here))
    }
  }, [status])

  if (status === 'loading') {
    return <BrandedSplash message="Loading session…" />
  }

  if (status === 'unauthenticated') {
    // Effect above handles the redirect; render a neutral message in
    // the frame between render and navigation.
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center p-6 text-sm text-gray-600"
      >
        Redirecting to sign in…
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center p-6 text-sm text-red-700"
      >
        <div className="max-w-md space-y-2 text-center">
          <h1 className="text-lg font-semibold">Could not verify session</h1>
          <p>{error?.message ?? 'Unknown error contacting the server.'}</p>
        </div>
      </div>
    )
  }

  if (user === undefined || user.role !== 'admin') {
    return <UnauthorizedPage role={user?.role} />
  }

  return <>{children}</>
}
